import { Button, Input, Modal, Typography } from '@replai-platform/ui-components';
import styled from 'styled-components';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as SDK from '@replai-platform/sdk';
import { CellData, GeneratedColumn, RequiredGeneratorParams } from '../../common';
import ColumnHeader from '../../generateHeader';
import useSaveReportAsTemplate from '../../../../api/hooks/reports/useSaveReportAsTemplate';
import { RootState } from '../../../../store/rootReducer';
import { TypographyClean } from '../../../../utils/styles';

const ButtonContainer = styled.div`
  display: flex;
  min-width: 0;
`;

const TemplateName = styled(TypographyClean)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type ReportSaveTemplateAccessorType = {
  reportId: string;
  templateId?: string;
  templateName?: string;
  reportType?: SDK.ReportTypes;
  externalId?: string;
};

const ReportTypeCellDecorator: React.FC<{
  cellData: ReportSaveTemplateAccessorType;
}> = ({ cellData }) => {
  const projectId = useSelector((state: RootState) => state.project.id);
  const [isEnabled, setIsEnabled] = useState(!!cellData?.templateId);
  const [modalOpen, setModalOpen] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const saveReportAsTemplateMutation = useSaveReportAsTemplate();

  if (!cellData) {
    return null;
  }

  return (
    <ButtonContainer>
      <Modal
        className="report-save-template-modal"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onClose={() => setModalOpen(false)}
        isOpen={modalOpen}
      >
        <Typography>Type the template name:</Typography>
        <Input
          key="template-id"
          onChange={(e) => {
            setTemplateName(e.target.value ?? '');
          }}
          fullWidth
          placeholder="UA Report"
          leadingIcon="Tag"
        />
        <Button
          disabled={(templateName?.length ?? 0) === 0 || isButtonLoading}
          style={{ marginTop: '1rem' }}
          trailingIcon={{ name: 'Send' }}
          onClick={() => {
            setIsButtonLoading(true);

            saveReportAsTemplateMutation
              .mutateAsync({
                projectId,
                reportId: cellData.reportId,
                name: templateName,
              })
              .then(() => {
                setModalOpen(false);
                setIsEnabled(true);
              })
              .catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e);
              })
              .finally(() => {
                // Without this a failed save leaves the button disabled until the page reloads
                setIsButtonLoading(false);
              });
          }}
        >
          Save template
        </Button>
      </Modal>
      {!isEnabled ? (
        // Secondary so the row keeps a single primary action ('Open'), and a bookmark so it isn't
        // mistaken for the favorites star
        <Button
          variant="outlined"
          trailingIcon={{ name: 'Bookmark' }}
          onClick={() => {
            setModalOpen(true);
          }}
        >
          Save template
        </Button>
      ) : (
        <TemplateName title={cellData.templateName ?? templateName}>
          {cellData.templateName ?? templateName}
        </TemplateName>
      )}
    </ButtonContainer>
  );
};

function getReportSaveTemplateColumn<T extends object = ReportSaveTemplateAccessorType>({
  columnId,
  accessor,
}: RequiredGeneratorParams<T, ReportSaveTemplateAccessorType>): GeneratedColumn<T> {
  return {
    id: columnId,
    accessor,
    Header: <ColumnHeader title="Template" />,
    Cell: (data: CellData<T, ReportSaveTemplateAccessorType>) => (
      <ReportTypeCellDecorator cellData={data?.cell?.value} />
    ),
  };
}

export default getReportSaveTemplateColumn;
